import { Point } from 'geojson';
import { Observable } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { Layer } from '~app/shared/map/layer/layer.model';
import { MapElement } from '~app/shared/map/map-element.model';
import { MapService } from '~app/shared/map/map.service';
import { FluentBaseResult, FluentDatasource } from './fluent-interfaces';

export class FluentLayerHelper {
  private equipmentMarkers$: Observable<MapElement<Point>[]>;
  private layerMap: Map<string, Layer> = new Map();
  private equipmentLayerVisible = false;
  private dataLoaded = false;
  private readonly EQUIPMENT_LAYER_ID = 'Kalusto';

  constructor(
    private mapService: MapService,
    private readonly fluent: FluentDatasource,
    private ngUnsubscribe$: Observable<any>
  ) {

  }

  public async init() {
    this.fluent.init(() => this.equipmentLayerVisible);

    // listen to layer toggle events and join/leave room accordingly
    this.mapService.layersR$.pipe(
      filter(layer => layer.id === this.EQUIPMENT_LAYER_ID),
      switchMap(layer => layer.isVisible$),
      takeUntil(this.ngUnsubscribe$)
    ).subscribe({
      next: (isVisible) => {
        this.equipmentLayerVisible = isVisible.layerVisible;
        const equipmentLayer = this.layerMap.get(this.EQUIPMENT_LAYER_ID);

        if(isVisible) {
          if(!this.dataLoaded) {
            equipmentLayer.setLoadedStatus(false);
          }

          this.fluent.subscribe();
        } else {
          // always hide loading spinner if layer de-activated as no data is received in the background
          equipmentLayer.setLoadedStatus(true);
          this.fluent.unsubscribe();
        }

        this.fluent.subscribe();
      }
    });

    // store all layers once received
    this.mapService.layers$.pipe(
      takeUntil(this.ngUnsubscribe$)
    ).subscribe(layers => {
      layers.forEach(layer => {
        this.layerMap.set(layer.id, layer);
      });
    });

    this.equipmentMarkers$ = this.fluent.data$.pipe(
      map(data => {
        const equipmentLayer = this.layerMap.get(this.EQUIPMENT_LAYER_ID);
        if(!equipmentLayer) {
          return [];
        }
        // remove loading spinner once we have data
        equipmentLayer.setLoadedStatus(true);
        this.dataLoaded = true;
        return this.createMapElements(data.features, equipmentLayer);
      }),
      takeUntil(this.ngUnsubscribe$)
    );

    this.equipmentMarkers$.pipe(
      takeUntil(this.ngUnsubscribe$)
    ).subscribe({
      next: (vehicleMarkers) => this.addMarkers(vehicleMarkers)
    });
  }

  private createMapElements(features: FluentBaseResult['features'], layer: Layer) {
    return features.map(feature => {
      const getId = () => {
        const carid = feature.properties.carid;
        return carid && carid.length > 0 ? carid : feature.id;
      };
      const convertToNumber = (prop: string) => {
        return feature.properties[prop] ? +feature.properties[prop] : undefined
      };

      const newProperties = {
        ...feature.properties,
        // needed in order for mapbox expressions to be able to calculate icon rotation
        speed: convertToNumber('speed'),
        course: convertToNumber('course'),
      }

      return new MapElement<Point>(getId(), layer, newProperties, feature.type, feature.geometry);
    })
  }

  private addMarkers(equipmentMarkers: MapElement<Point>[]) {
    const map = new Map<Layer, MapElement<Point>[]>();
    equipmentMarkers.forEach(marker => {
      const key = marker.layer;
      if (map.has(key)) {
        map.get(key).push(marker);
      } else {
        map.set(key, [marker]);
      }
    });
    map.forEach((markers, layer) => {
      layer.pushFeatures(markers);
    });
  }

}

